class JsonAnimation extends HTMLElement {
  constructor() {
    super();
    this.animation = null;
    this.currentUrl = null;
    this.observer = null;
    this.resizeTimeout = null;
    this._initialized = false;
    this._onResize = this._onResize.bind(this);
    this._onClick = this._onClick.bind(this);
  }

  connectedCallback() {
    if (this._initialized) return;

    if (!window.lottie || !window.lottie.loadAnimation) {
      const lottieScript = document.querySelector('script[src*="lottie"]');
      if (lottieScript) {
        lottieScript.addEventListener('load', () => {
          if (this.isConnected) this.connectedCallback();
        }, { once: true });
      } else {
        setTimeout(() => {
          if (this.isConnected) this.connectedCallback();
        }, 50);
      }
      return;
    }

    this._initialized = true;

    this.desktopUrl = this.getAttribute('animation-url') || this.dataset.animationUrl || '';
    this.mobileUrl = this.getAttribute('animation-url-mobile') || this.dataset.animationUrlMobile || '';

    if (!this.desktopUrl) {
      console.warn('<json-animation> missing animation-url');
      return;
    }

    this.loop = this.#getBoolAttr('loop', true);
    this.autoplay = this.#getBoolAttr('autoplay', true);
    this.clickToPlay = this.#getBoolAttr('click-to-play', false);
    this.speed = parseFloat(this.getAttribute('speed') || this.dataset.speed) || 1;

    if (this.clickToPlay) {
      this.style.cursor = 'pointer';
      this.addEventListener('click', this._onClick);
    }

    this._loadAnimation();
    window.addEventListener('resize', this._onResize);
  }

  disconnectedCallback() {
    this._initialized = false;
    this.currentUrl = null;

    window.removeEventListener('resize', this._onResize);
    this.removeEventListener('click', this._onClick);

    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }

    if (this.animation) {
      this.animation.destroy();
      this.animation = null;
    }

    clearTimeout(this.resizeTimeout);
  }

  #getBoolAttr(name, defaultValue) {
    if (!this.hasAttribute(name)) return defaultValue;
    const value = this.getAttribute(name);
    return value === '' || value === null || value === 'true' || value === '1';
  }

  #isMobile() {
    return window.innerWidth < 750;
  }

  #getAnimationUrl() {
    return this.#isMobile() && this.mobileUrl ? this.mobileUrl : this.desktopUrl;
  }

  _onResize() {
    clearTimeout(this.resizeTimeout);
    this.resizeTimeout = setTimeout(() => {
      this._loadAnimation();
    }, 250);
  }

  _onClick() {
    if (!this.animation) return;
    if (this.animation.isPaused) {
      this.animation.play();
    } else {
      this.animation.pause();
    }
  }

  #setupObserver() {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }

    if (this.autoplay) return;

    this.observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!this.animation) return;

          if (entry.isIntersecting) {
            this.animation.play();
            if (!this.loop) this.observer.unobserve(this);
          } else if (this.loop) {
            this.animation.pause();
          }
        }
      },
      { threshold: 0.3 }
    );

    this.observer.observe(this);
  }

  _loadAnimation() {
    const url = this.#getAnimationUrl();
    if (!url) return;

    if (this.currentUrl === url && this.animation) return;

    this.currentUrl = url;

    if (this.animation) {
      this.animation.destroy();
      this.animation = null;
    }

    this.innerHTML = '';

    this.animation = window.lottie.loadAnimation({
      container: this,
      renderer: 'svg',
      loop: this.loop,
      autoplay: this.autoplay,
      path: url,
    });

    this.animation.setSpeed(this.speed);
    this.#setupObserver();
  }
}

customElements.define('json-animation', JsonAnimation);
